import { pipe } from "fp-ts/lib/function";
import * as t from "io-ts";
import { fold } from "fp-ts/lib/Either";
import {
  FOOTER_SECTION_TYPE,
  getSectionTypes,
  HEADER_SECTION_TYPE
} from "@/lib/singularity/singularity";
import { SectionDescription, Sitemap, SitemapV } from "@/types/singularity";
import { getTreeFromSectionDescriptions } from "./sections";
import { globalStore, singularityStore, uiStore } from "@/store/store";
import {
  loadBuilderElementsFromWordPressWithPromise,
  replaceDocumentWithAnother
} from "@/lib/wordpress/load";
import { loadSingularitySectionLibrary } from "@/lib/singularity/page-generation";
import { requestStructuredOutput } from "@/lib/ai/structured-outputs";
import { BREAKDANCE_BLOCK_POST_TYPE } from "@/lib/constants/post-types";
import { SingularityMeta } from "@/types/document";
import { ExportedTree } from "@/types/tree";
import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import { fpJsonStringify } from "@/util/fp-json-stringify";
import { jsonStringifyOrUndefined } from "@/util/json-stringify-or-undefined";
import { exportTree } from "@/lib/tree/tree";
import { PageInWP } from "@/lib/singularity/wp-page-manager/types";
import { TemplateSettings } from "@/subapps/manage-templates/types";
import { backgroundPageGenerationService } from "./pages/background-page-generation-service";
import {
  FutureLayerAISettings,
  saveFutureLayerAISettingsToGlobalStoreAndWordPress
} from "./settings/settings";
import {
  addHeaderAndFooterNodesToTree,
  getElementIdsForHeaderAndFooterNodeInCurrentDocument
} from "./headers-footers/headers-footers";
import { getBlocksAndPutIntoStore } from "@/lib/global-blocks/global-blocks";
import {
  addAiJobAndGetValueReturnedByCallback,
  aiJobController
} from "@/futurelayer/ai-job-manager/ai-jobs";
import { setUi } from "@/lib/singularity/ui";

export async function step1ButtonClicked(aiSettings: FutureLayerAISettings) {
  // unnecessary as it is already called in Step1.vue mounted
  // await maybeThingsWeNeedToDoFirst()

  const timeElapsed = getTimeElapsedFunction();

  // saving to WordPress slows things down, we should just save to global store, and then later, AFTER we show the user the generated page, we save to WP
  await saveFutureLayerAISettingsToGlobalStoreAndWordPress(aiSettings);

  const sitemap = await generateSitemapButOnlyIncludeSectionsForHomepage();
  console.log(
    "generateSitemapButOnlyIncludeSectionsForHomepage done",
    timeElapsed()
  );

  if (!sitemap) {
    console.log("no sitemap");
    return;
  }

  const homepageData = generateHomepageTree(sitemap);
  if (!homepageData) {
    console.log("no homepageData");
    return;
  }

  const headerData = generateHeaderPageData();
  const footerData = generateFooterPageData();

  const response =
    await createHomepageHeaderAndFooter_andDeleteOldPages_andSetHomepageToFrontPage_andCreateOtherPages(
      homepageData,
      headerData,
      footerData,
      removeHomepageFromSitemapPages(sitemap),
      getPageTitlesForMainMenu(sitemap)
    );

  console.log(
    "createHomepageHeaderAndFooter_andDeleteOldPages_andSetHomepageToFrontPage_andCreateOtherPages done",
    timeElapsed()
  );

  if (!response) return;

  const pagesInWP: PageInWP[] = [];

  pagesInWP.push({
    id: response.homepage.id,
    title: response.homepage.title,
    relativeUrl: response.homepage.relativeUrl,
    postType: "page",
    singularityMeta: homepageData.singularityMeta
      ? homepageData.singularityMeta
      : false,
    generatingInBackground: false,
    isInMainMenu: response.homepage.isInMainMenu,
    menuOrder: response.homepage.menuOrder
  });

  response.otherPages.forEach(page => {
    pagesInWP.push({
      id: page.id,
      title: page.title,
      relativeUrl: page.relativeUrl,
      postType: "page",
      singularityMeta: false,
      generatingInBackground: false,
      isInMainMenu: page.isInMainMenu,
      menuOrder: page.menuOrder
    });
  });

  singularityStore.setPagesInWP(pagesInWP);

  // we queue up the background generation jobs but don't run them
  // we unpause this job type once the page sections mount in the dom and begin generating
  aiJobController.pauseJobType("background_page_generation");

  singularityStore.setHeaderAndFooterBlockIdsInWP({
    headerId: response.header.id,
    footerId: response.footer.id
  });

  backgroundPageGenerationService.startBackgroundGenerationForGlobalBlock(
    response.header.id,
    () => {
      return getElementIdsForHeaderAndFooterNodeInCurrentDocument()?.headerId;
    }
  );
  backgroundPageGenerationService.startBackgroundGenerationForGlobalBlock(
    response.footer.id,
    () => {
      return getElementIdsForHeaderAndFooterNodeInCurrentDocument()?.footerId;
    }
  );

  uiStore.setUnsavedChangesPresent(false);
  await replaceDocumentWithAnother(response.homepage.id);
  console.log("replaceDocumentWithAnother done", timeElapsed());

  setTimeout(() => {
    singularityStore.pagesInWP.forEach(page => {
      if (page.id !== response.homepage.id) {
        backgroundPageGenerationService.startBackgroundGenerationForPage(
          page.title,
          page.id
        );
      }
    });
  }, 10000);

  setUi("step2");
}

function findHomepageInSitemap(sitemap: Sitemap) {
  return sitemap.find(page => {
    return page.title.toLowerCase().includes("home");
  });
}

export async function generateSitemapButOnlyIncludeSectionsForHomepage(
  additionalPromptInfo = ""
): Promise<Sitemap | undefined> {
  const websiteName = globalStore.aiSettings.prompt.businessName ?? "";
  const websiteDescription =
    globalStore.aiSettings.prompt.businessDescription ?? "";

  const sectionTypesString = getSectionTypes()
    .map(sectionType => `'${sectionType}'`)
    .join(", ");

  let prompt = ``;

  if (!additionalPromptInfo) {
    prompt = `Generate a sitemap for a website.
          The website should have an appropriate number of pages for the content. If the prompt includes information about the services offered, consider individual pages for each service.

          The sitemap must contain a homepage called "Home".

          Each page should have an appropriate number of sections for its type. Maybe 8 for the "Home" page, and just one or two for the contact page.

          However, only provide the sections for the "Home" page. For all other pages, the sections should be a blank array.

          The homepage hero section must be of the type "Image Background Heroes".

          You must still include the other pages, just not their sections.

          Include up to 5 pages in the main menu, and only exclude a page from the menu if it really doesn't belong. Err on the side of inlcuding pages in the menu.

          Website name: ${websiteName}
          Website description: ${websiteDescription}

          Valid values for 'type' are ${sectionTypesString}. It's important you don't use any other values for 'type'.
          `;
  } else {
    prompt = `Generate a sitemap for a website.

          ${
            additionalPromptInfo
              ? `
                  ${additionalPromptInfo}
                  `
              : ""
          }

          Valid values for 'type' are ${sectionTypesString}. It's important you don't use any other values for 'type'.`;
  }

  const result = await addAiJobAndGetValueReturnedByCallback(
    async abortController => {
      return await requestStructuredOutput(
        prompt,
        SitemapV,
        abortController,
        true
      );
    },
    {
      jobType: "sitemap_generation",
      businessName: websiteName,
      businessDescription: websiteDescription,
      description: `Generating sitemap with homepage sections for ${
        websiteName ?? "website"
      }`,
      priority: "critical"
    }
  );

  return result;
}

export function generateHomepageTree(sitemap: Sitemap): PageData | undefined {
  const homepage = findHomepageInSitemap(sitemap);
  if (!homepage) {
    console.error("no homepage found in sitemap");
    return;
  }

  const noHeaderOrFooterTree = getTreeFromSectionDescriptions(
    homepage.sections
  );

  const homepageTree = addHeaderAndFooterNodesToTree(noHeaderOrFooterTree);
  return {
    title: homepage.title,
    postType: "page",
    tree: exportTree(homepageTree),
    singularityMeta: {
      singularityInitialSections: homepage.sections,
      initialSectionsAlreadyPopulated: true
    }
  };
}

export async function createHomepageHeaderAndFooter_andDeleteOldPages_andSetHomepageToFrontPage_andCreateOtherPages(
  homepageData: PageData,
  headerData: PageData,
  footerData: PageData,
  titlesForAdditionalPagesToCreate: string[],
  titlesForPagesToIncludeInMainMenu: string[]
) {
  return pipe(
    await makeWordPressAjaxRequest({
      action: "breakdance_singularity_handle_step_1",
      decoder: t.type({
        success: t.string,
        homepage: t.type({
          id: t.number,
          title: t.string,
          relativeUrl: t.string,
          isInMainMenu: t.boolean,
          menuOrder: t.union([t.number, t.null])
        }),
        header: t.type({
          id: t.number
        }),
        footer: t.type({
          id: t.number
        }),
        otherPages: t.array(
          t.type({
            id: t.number,
            title: t.string,
            relativeUrl: t.string,
            isInMainMenu: t.boolean,
            menuOrder: t.union([t.number, t.null])
          })
        )
      }),
      dataToPost: {
        homepageData: pageDataToJsonStringWithNestedJsonStrings(homepageData),
        headerData: pageDataToJsonStringWithNestedJsonStrings(headerData),
        footerData: pageDataToJsonStringWithNestedJsonStrings(footerData),
        titlesForAdditionalPagesToCreate: titlesForAdditionalPagesToCreate,
        titlesForPagesToIncludeInMainMenu: titlesForPagesToIncludeInMainMenu
      }
    }),
    fold(
      () => null,
      response => {
        return response;
      }
    )
  );
}

type PageData = {
  title: string;
  postType: "page" | typeof BREAKDANCE_BLOCK_POST_TYPE;
  singularityMeta?: SingularityMeta;
  tree?: ExportedTree;
  templateSettings?: TemplateSettings;
};

function pageDataToJsonStringWithNestedJsonStrings(pageData: PageData) {
  return fpJsonStringify({
    title: pageData.title,
    postType: pageData.postType,
    singularityMeta: jsonStringifyOrUndefined(pageData.singularityMeta),
    tree: jsonStringifyOrUndefined(pageData.tree),
    templateSettings: jsonStringifyOrUndefined(pageData.templateSettings)
  });
}

export function generateHeaderPageData(): PageData {
  const section: SectionDescription = {
    type: HEADER_SECTION_TYPE,
    sectionFriendlyName: "Header",
    description: "Header"
  };

  const tree = getTreeFromSectionDescriptions([section]);

  return {
    title: "Header",
    postType: BREAKDANCE_BLOCK_POST_TYPE,
    tree: exportTree(tree),
    singularityMeta: {
      singularityInitialSections: [section],
      initialSectionsAlreadyPopulated: true
    }
  };
}

export function generateFooterPageData(): PageData {
  const section: SectionDescription = {
    type: FOOTER_SECTION_TYPE,
    sectionFriendlyName: "Footer",
    description: "Footer"
  };

  const tree = getTreeFromSectionDescriptions([section]);

  return {
    title: "Footer",
    postType: BREAKDANCE_BLOCK_POST_TYPE,
    tree: exportTree(tree),
    singularityMeta: {
      singularityInitialSections: [section],
      initialSectionsAlreadyPopulated: true
    }
  };
}

export async function maybeThingsWeNeedToDoFirst() {
  // need to do this before we can use the tree
  await loadBuilderElementsFromWordPressWithPromise();
  // might be unnecessary as this might already be loaded
  await loadSingularitySectionLibrary();
}

export function getTimeElapsedFunction() {
  return (function () {
    const start = new Date().getTime();
    return function () {
      const end = new Date().getTime();
      const time = end - start;
      return time;
    };
  })();
}

function removeHomepageFromSitemapPages(sitemap: Sitemap) {
  return sitemap
    .map(page => page.title)
    .filter(title => !title.toLowerCase().includes("home"));
}

function getPageTitlesForMainMenu(sitemap: Sitemap) {
  return sitemap.filter(page => page.includeInMainMenu).map(page => page.title);
}
